import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./EnvironmentEngagementTable.scss";

/**
 * EnvironmentEngagementTable
 * 
 * A sortable table showing engagement metrics for each environment.
 * Helps teachers identify which environments need attention.
 * 
 * Decision it enables: "Which environments are engaging students well vs poorly?"
 */
export default function EnvironmentEngagementTable({ 
  detailedSessions = [], 
  insights = [],
  environmentData = {},
  limit = 10
}) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState("visits");
  const [sortDirection, setSortDirection] = useState("desc");

  const environmentStats = useMemo(() => {
    if (!detailedSessions || detailedSessions.length === 0) {
      return [];
    }

    // Aggregate stats per environment
    const envStats = {};

    detailedSessions.forEach(session => {
      const sessionEnvs = new Set();
      let lastEnvId = null;
      
      (session.events || []).forEach(event => {
        const envId = event.environmentId;
        if (!envId) return;

        // Initialize environment stats
        if (!envStats[envId]) {
          envStats[envId] = {
            environmentId: envId,
            environmentName: environmentData[envId] || `Environment ${envId.slice(0, 8)}...`,
            visits: 0,
            uniqueVisitors: new Set(),
            totalTime: 0,
            timeEvents: 0,
            assessments: 0,
            totalScore: 0,
            scoredAssessments: 0,
            dropOffs: 0, // Sessions that ended here
          };
        }

        // Count visit (only once per session per environment)
        if (!sessionEnvs.has(envId)) {
          envStats[envId].visits++;
          sessionEnvs.add(envId);
          envStats[envId].uniqueVisitors.add(session.sessionCode);
        }

        // Track time spent (if available)
        if (event.timeSpent || event.duration) {
          envStats[envId].totalTime += event.timeSpent || event.duration || 0;
          envStats[envId].timeEvents++;
        }

        // Track assessments
        if ((event.pk || "").includes("Assessment")) {
          envStats[envId].assessments++;
          const score = parseFloat(event.score);
          if (!isNaN(score)) {
            envStats[envId].totalScore += score;
            envStats[envId].scoredAssessments++;
          }
        }

        lastEnvId = envId;
      });

      // Track drop-off (last environment in session)
      if (lastEnvId && envStats[lastEnvId]) {
        envStats[lastEnvId].dropOffs++;
      }
    });

    // Calculate derived metrics
    return Object.values(envStats)
      .map(env => ({
        ...env,
        uniqueVisitors: env.uniqueVisitors.size,
        avgTime: env.timeEvents > 0 
          ? Math.round(env.totalTime / env.timeEvents) 
          : null,
        avgScore: env.scoredAssessments > 0 
          ? Math.round(env.totalScore / env.scoredAssessments) 
          : null,
        dropOffRate: env.visits > 0 
          ? Math.round((env.dropOffs / env.visits) * 100) 
          : 0,
        engagementScore: calculateEngagementScore(env),
      }))
      .filter(env => env.visits > 0);
  }, [detailedSessions, environmentData]);

  // Calculate a simple engagement score (0-100)
  function calculateEngagementScore(env) {
    let score = 0;
    
    // More visitors = better (up to 30 points)
    score += Math.min(env.uniqueVisitors.size * 3, 30);
    
    // Higher avg score = better (up to 40 points)
    if (env.scoredAssessments > 0) {
      score += (env.totalScore / env.scoredAssessments) * 0.4;
    }
    
    // Lower drop-off = better (up to 30 points)
    const dropOffRate = env.visits > 0 ? env.dropOffs / env.visits : 1;
    score += (1 - dropOffRate) * 30;
    
    return Math.round(score);
  }

  // Sort the data
  const sortedData = useMemo(() => {
    const sorted = [...environmentStats].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null values
      if (aVal === null) aVal = -Infinity;
      if (bVal === null) bVal = -Infinity;
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return sorted.slice(0, limit);
  }, [environmentStats, sortField, sortDirection, limit]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const getScoreColor = (score) => {
    if (score === null) return "neutral";
    if (score >= 70) return "high";
    if (score >= 40) return "mid";
    return "low";
  };

  const getDropOffColor = (rate) => {
    if (rate >= 60) return "high"; // High drop-off is bad
    if (rate >= 40) return "mid";
    return "low"; // Low drop-off is good
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "—";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleRowClick = (env) => {
    // Find first insight in this environment
    const insight = insights.find(i => i.environmentId === env.environmentId);
    if (insight) {
      navigate(`/question?insightId=${encodeURIComponent(insight.insightId)}&q=${encodeURIComponent(insight.textValue || '')}`);
    }
  };

  if (sortedData.length === 0) {
    return (
      <div className="envEngagement">
        <div className="eeEmpty">
          <div className="eeEmptyIcon">📊</div>
          <div className="eeEmptyText">No environment data available</div>
          <div className="eeEmptySub">Data will appear when students visit environments</div>
        </div>
      </div>
    );
  }

  return (
    <div className="envEngagement">
      {/* Table */}
      <div className="eeTableWrapper">
        <table className="eeTable">
          <thead>
            <tr>
              <th className="eeThEnv">Environment</th>
              <th 
                className="eeThSortable"
                onClick={() => handleSort("visits")}
              >
                Visits {getSortIcon("visits")}
              </th>
              <th 
                className="eeThSortable"
                onClick={() => handleSort("uniqueVisitors")}
              >
                Students {getSortIcon("uniqueVisitors")}
              </th>
              <th 
                className="eeThSortable"
                onClick={() => handleSort("assessments")}
              >
                Responses {getSortIcon("assessments")}
              </th>
              <th 
                className="eeThSortable"
                onClick={() => handleSort("avgScore")}
              >
                Avg Score {getSortIcon("avgScore")}
              </th>
              <th 
                className="eeThSortable"
                onClick={() => handleSort("dropOffRate")}
              >
                Drop-off {getSortIcon("dropOffRate")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((env, idx) => (
              <tr 
                key={env.environmentId}
                className="eeRow"
                onClick={() => handleRowClick(env)}
              >
                <td className="eeTdEnv">
                  <span className="eeEnvRank">{idx + 1}</span>
                  <span className="eeEnvName" title={env.environmentName}>
                    {env.environmentName}
                  </span>
                </td>
                <td className="eeTdNum">{env.visits}</td>
                <td className="eeTdNum">{env.uniqueVisitors}</td>
                <td className="eeTdNum">{env.assessments}</td>
                <td className="eeTdNum">
                  {env.avgScore !== null ? (
                    <span className={`eeScore eeScore--${getScoreColor(env.avgScore)}`}>
                      {env.avgScore}%
                    </span>
                  ) : (
                    <span className="eeNoData">—</span>
                  )}
                </td>
                <td className="eeTdNum">
                  <span className={`eeDropOff eeDropOff--${getDropOffColor(env.dropOffRate)}`}>
                    {env.dropOffRate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="eeLegend">
        <div className="eeLegendItem">
          <span className="eeLegendLabel">Drop-off:</span>
          <span className="eeLegendValue">% of sessions that ended in this environment</span>
        </div>
      </div>

      {/* Footer */}
      <div className="eeFooter">
        Click any row to explore questions in that environment
      </div>
    </div>
  );
}
